import { LoggedUser } from './LoginModal';

export interface AgencyItem {
  id: string;
  name: string;
  code: string;
  description: string;
  active: boolean;
  usersCount: number;
  casesCount: number;
}

export interface RoleItem {
  id: string;
  name: string;
  code: string;
  description: string;
  can_dispatch: boolean;
  can_view_cases: boolean;
  can_view_videos: boolean;
  can_manage_retention: boolean;
  can_manage_users: boolean;
}

export interface UserItem {
  id: string;
  username: string;
  fullName: string;
  badgeNumber: string;
  role: string;
  agencyId: string;
  agencyName: string;
  agencyCode: string;
  active: boolean;
  createdAt: string;
}

export class AdminConsoleView {
  private container: HTMLElement;
  private apiBaseUrl: string;
  private currentUser: LoggedUser;
  private activeTab: 'overview' | 'agencies' | 'roles' | 'users' | 'storage' = 'overview';

  private agencies: AgencyItem[] = [];
  private roles: RoleItem[] = [];
  private users: UserItem[] = [];
  private metrics: any = {};
  private retentionPolicy = { retentionDays: 90, autoPurgeEnabled: true };

  constructor(container: HTMLElement, apiBaseUrl: string, currentUser: LoggedUser) {
    this.container = container;
    this.apiBaseUrl = apiBaseUrl;
    this.currentUser = currentUser;
  }

  public async render(): Promise<void> {
    this.container.innerHTML = `
      <div style="display:flex; flex-direction:column; height:100%; width:100%; background:#0b1120; color:#f8fafc; font-family:var(--font-sans, system-ui, sans-serif); overflow:hidden;">
        
        <!-- Header de Administración -->
        <div style="background:#0f172a; border-bottom:1px solid #1e293b; padding:16px 24px; display:flex; justify-content:space-between; align-items:center;">
          <div style="display:flex; align-items:center; gap:14px;">
            <div style="width:42px; height:42px; border-radius:12px; background:linear-gradient(135deg, #d97706, #b45309); display:flex; align-items:center; justify-content:center; font-size:1.4rem; box-shadow:0 4px 14px rgba(217,119,6,0.3);">
              ⚙️
            </div>
            <div>
              <h1 style="margin:0; font-size:1.3rem; font-weight:800; letter-spacing:-0.5px;">Panel Central de Gobierno y Administración Tecnológica</h1>
              <div style="font-size:0.8rem; color:#94a3b8; margin-top:2px;">
                Multi-Tenant: Aislamiento por Agencia • Control Granular de Roles • Cuotas y Retención de Video
              </div>
            </div>
          </div>

          <div style="display:flex; align-items:center; gap:12px;">
            <div style="background:rgba(217,119,6,0.15); border:1px solid rgba(217,119,6,0.4); border-radius:8px; padding:6px 14px; text-align:right;">
              <div style="font-size:0.7rem; color:#fbbf24; font-weight:700; text-transform:uppercase;">Sesión Administrador Global</div>
              <div style="font-size:0.85rem; font-weight:700; color:#fff;">${this.currentUser.fullName} (${this.currentUser.username})</div>
            </div>
          </div>
        </div>

        <!-- Barra de Navegación de Pestañas Administrativas -->
        <div class="admin-tabs-nav">
          <button class="admin-tab-btn ${this.activeTab === 'overview' ? 'active' : ''}" data-tab="overview">
            <span>📊</span> Resumen Tecnológico & Espacio
          </button>
          <button class="admin-tab-btn ${this.activeTab === 'agencies' ? 'active' : ''}" data-tab="agencies">
            <span>🏢</span> Agencias & Clientes <span class="tab-badge" id="badge-agencies-count">${this.agencies.length || '0'}</span>
          </button>
          <button class="admin-tab-btn ${this.activeTab === 'roles' ? 'active' : ''}" data-tab="roles">
            <span>🛡️</span> Perfiles & Permisos Granulares
          </button>
          <button class="admin-tab-btn ${this.activeTab === 'users' ? 'active' : ''}" data-tab="users">
            <span>👥</span> Usuarios & Asignación <span class="tab-badge" id="badge-users-count">${this.users.length || '0'}</span>
          </button>
          <button class="admin-tab-btn ${this.activeTab === 'storage' ? 'active' : ''}" data-tab="storage">
            <span>🗄️</span> Retención & Purga de Video
          </button>
        </div>

        <!-- Cuerpo Dinámico de la Pestaña -->
        <div id="admin-view-content" style="flex:1; overflow-y:auto; padding:24px;">
          <div style="text-align:center; padding:60px; color:#64748b;">Cargando información del sistema...</div>
        </div>
      </div>
    `;

    this.setupTabListeners();
    await this.loadInitialData();
  }

  private setupTabListeners(): void {
    const btns = this.container.querySelectorAll('.admin-tab-btn');
    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        btns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.activeTab = btn.getAttribute('data-tab') as any;
        this.renderActiveTabContent();
      });
    });
  }

  private async loadInitialData(): Promise<void> {
    try {
      const [resAgencies, resRoles, resUsers, resMetrics, resPolicy] = await Promise.all([
        fetch(`${this.apiBaseUrl}/api/v1/auth/agencies`).then(r => r.json()).catch(() => ({ data: [] })),
        fetch(`${this.apiBaseUrl}/api/v1/auth/roles`).then(r => r.json()).catch(() => ({ data: [] })),
        fetch(`${this.apiBaseUrl}/api/v1/auth/users`).then(r => r.json()).catch(() => ({ data: [] })),
        fetch(`${this.apiBaseUrl}/api/v1/media/metrics`).then(r => r.json()).catch(() => ({ data: {} })),
        fetch(`${this.apiBaseUrl}/api/v1/media/retention-policy`).then(r => r.json()).catch(() => ({ data: { retentionDays: 90, autoPurgeEnabled: true } }))
      ]);

      if (resAgencies.success) this.agencies = resAgencies.data;
      if (resRoles.success) this.roles = resRoles.data;
      if (resUsers.success) this.users = resUsers.data;
      if (resMetrics.success) this.metrics = resMetrics.data;
      if (resPolicy.success) this.retentionPolicy = resPolicy.data;

      // Actualizar contadores en badges de tabs
      const agencyBadge = this.container.querySelector('#badge-agencies-count');
      if (agencyBadge) agencyBadge.textContent = `${this.agencies.length}`;
      const userBadge = this.container.querySelector('#badge-users-count');
      if (userBadge) userBadge.textContent = `${this.users.length}`;

      this.renderActiveTabContent();
    } catch (err) {
      console.error('[AdminConsoleView] Error cargando datos iniciales:', err);
    }
  }

  private renderActiveTabContent(): void {
    const contentArea = this.container.querySelector('#admin-view-content');
    if (!contentArea) return;

    switch (this.activeTab) {
      case 'overview':
        this.renderOverviewTab(contentArea);
        break;
      case 'agencies':
        this.renderAgenciesTab(contentArea);
        break;
      case 'roles':
        this.renderRolesTab(contentArea);
        break;
      case 'users':
        this.renderUsersTab(contentArea);
        break;
      case 'storage':
        this.renderStorageTab(contentArea);
        break;
    }
  }

  // --- 1. RESUMEN TECNOLÓGICO Y ESPACIO EN DISCO ---
  private renderOverviewTab(area: Element): void {
    area.innerHTML = `
      <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap:18px; margin-bottom:24px;">
        <div style="background:#1e293b; border:1px solid #334155; border-radius:12px; padding:18px;">
          <div style="font-size:0.75rem; text-transform:uppercase; color:#94a3b8; font-weight:700;">Espacio Ocupado en Disco</div>
          <div style="font-size:1.9rem; font-weight:800; color:#38bdf8; margin:8px 0 4px;">${this.metrics.totalStorageMb || '0.00'} MB</div>
          <div style="font-size:0.75rem; color:#64748b;">En grabaciones forenses H.264 CRF</div>
        </div>

        <div style="background:#1e293b; border:1px solid #334155; border-radius:12px; padding:18px;">
          <div style="font-size:0.75rem; text-transform:uppercase; color:#94a3b8; font-weight:700;">Agencias Conectadas</div>
          <div style="font-size:1.9rem; font-weight:800; color:#fbbf24; margin:8px 0 4px;">${this.agencies.length}</div>
          <div style="font-size:0.75rem; color:#64748b;">Instancias aisladas (Multi-Tenant)</div>
        </div>

        <div style="background:#1e293b; border:1px solid #334155; border-radius:12px; padding:18px;">
          <div style="font-size:0.75rem; text-transform:uppercase; color:#94a3b8; font-weight:700;">Usuarios del Sistema</div>
          <div style="font-size:1.9rem; font-weight:800; color:#34d399; margin:8px 0 4px;">${this.users.length}</div>
          <div style="font-size:0.75rem; color:#64748b;">Operadores, supervisores y administradores</div>
        </div>

        <div style="background:#1e293b; border:1px solid #334155; border-radius:12px; padding:18px;">
          <div style="font-size:0.75rem; text-transform:uppercase; color:#94a3b8; font-weight:700;">Política de Retención Activa</div>
          <div style="font-size:1.9rem; font-weight:800; color:#f43f5e; margin:8px 0 4px;">${this.retentionPolicy.retentionDays} días</div>
          <div style="font-size:0.75rem; color:#64748b;">${this.retentionPolicy.autoPurgeEnabled ? '● Purga automática activa (03:00 hrs)' : '○ Purga manual'}</div>
        </div>
      </div>

      <!-- Resumen por Agencia -->
      <div style="background:#1e293b; border:1px solid #334155; border-radius:12px; padding:20px;">
        <h3 style="margin:0 0 14px; font-size:1.1rem; color:#fff;">Distribución Operativa por Agencia / Cliente</h3>
        <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap:14px;">
          ${this.agencies.map(a => `
            <div style="background:#0f172a; border:1px solid #334155; border-radius:10px; padding:16px;">
              <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:8px;">
                <span style="font-weight:800; font-size:1rem; color:#fff;">${a.name}</span>
                <span style="background:rgba(56,189,248,0.15); color:#38bdf8; font-size:0.72rem; font-weight:700; padding:2px 8px; border-radius:4px;">${a.code}</span>
              </div>
              <p style="font-size:0.8rem; color:#94a3b8; margin:0 0 12px; min-height:32px;">${a.description || 'Sin descripción'}</p>
              <div style="display:flex; justify-content:space-between; font-size:0.78rem; border-top:1px solid #1e293b; padding-top:10px;">
                <span>👥 Usuarios: <strong>${a.usersCount || 0}</strong></span>
                <span>📞 Casos atendidos: <strong>${a.casesCount || 0}</strong></span>
                <span>🔒 Aislamiento: <strong style="color:#10b981;">ESTRICTO</strong></span>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  // --- 2. GESTIÓN MULTI-TENANT: AGENCIAS Y CLIENTES ---
  private renderAgenciesTab(area: Element): void {
    area.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:18px;">
        <div>
          <h2 style="margin:0; font-size:1.2rem;">Agencias e Instituciones Registradas</h2>
          <p style="margin:4px 0 0; font-size:0.82rem; color:#94a3b8;">
            Cada agencia opera en un entorno hermético. Los supervisores y paramédicos de una agencia no pueden ver expedientes, videos ni ubicaciones de otra.
          </p>
        </div>
        <button id="btn-toggle-new-agency" class="admin-btn-primary">
          + REGISTRAR NUEVA AGENCIA
        </button>
      </div>

      <!-- Formulario para crear agencia -->
      <div id="form-new-agency-box" style="display:none; background:#1e293b; border:1px solid #38bdf8; border-radius:12px; padding:20px; margin-bottom:20px;">
        <h3 style="margin:0 0 14px; font-size:1rem; color:#38bdf8;">Registrar Nueva Agencia / Cliente Corporativo</h3>
        <div style="display:grid; grid-template-columns: 2fr 1fr; gap:14px; margin-bottom:14px;">
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Nombre Oficial de la Agencia</label>
            <input type="text" id="inp-agency-name" class="form-input" placeholder="ej. Centro Regulador de Urgencias Médicas / 911 Dispatch / EMS Agency" style="width:100%; box-sizing:border-box;" />
          </div>
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Código Único (Identificador Tenant)</label>
            <input type="text" id="inp-agency-code" class="form-input" placeholder="ej. POL-ESTATAL" style="width:100%; box-sizing:border-box;" />
          </div>
        </div>
        <div style="margin-bottom:14px;">
          <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Descripción o Giro Operativo</label>
          <input type="text" id="inp-agency-desc" class="form-input" placeholder="ej. Despacho táctico y primeros respondientes policiales" style="width:100%; box-sizing:border-box;" />
        </div>
        <div style="display:flex; justify-content:flex-end; gap:10px;">
          <button id="btn-cancel-agency" class="admin-action-btn" style="background:transparent; border:1px solid #475569; color:#cbd5e1;">Cancelar</button>
          <button id="btn-save-agency" class="admin-btn-success">Dar de Alta Agencia</button>
        </div>
      </div>

      <!-- Tabla de Agencias -->
      <div style="background:#1e293b; border:1px solid #334155; border-radius:10px; overflow:hidden;">
        <table style="width:100%; border-collapse:collapse; text-align:left; font-size:0.85rem;">
          <thead>
            <tr style="background:#0f172a; color:#94a3b8; font-size:0.75rem; text-transform:uppercase;">
              <th style="padding:12px 16px;">Código</th>
              <th style="padding:12px 16px;">Nombre de la Agencia</th>
              <th style="padding:12px 16px;">Descripción</th>
              <th style="padding:12px 16px;">Usuarios Activos</th>
              <th style="padding:12px 16px;">Casos Registrados</th>
              <th style="padding:12px 16px;">Estado</th>
              <th style="padding:12px 16px; text-align:right;">Acciones</th>
            </tr>
          </thead>
          <tbody>
            ${this.agencies.map(a => `
              <tr style="border-bottom:1px solid #334155;">
                <td style="padding:12px 16px; font-family:monospace; font-weight:700; color:#38bdf8;">${a.code}</td>
                <td style="padding:12px 16px; font-weight:700; color:#fff;">${a.name}</td>
                <td style="padding:12px 16px; color:#cbd5e1;">${a.description || '---'}</td>
                <td style="padding:12px 16px; color:#cbd5e1;">${a.usersCount || 0}</td>
                <td style="padding:12px 16px; color:#cbd5e1;">${a.casesCount || 0}</td>
                <td style="padding:12px 16px;">
                  <span style="color:${a.active ? '#10b981' : '#ef4444'}; font-weight:700; font-size:0.8rem;">
                    ● ${a.active ? 'Operativa' : 'Inactiva / Suspendida'}
                  </span>
                </td>
                <td style="padding:12px 16px; text-align:right;">
                  <div style="display:flex; justify-content:flex-end; gap:8px;">
                    <button class="btn-toggle-agency admin-action-btn ${a.active ? 'btn-warn' : 'btn-success'}" data-id="${a.id}" data-active="${a.active}" data-name="${a.name}">
                      ${a.active ? 'Inactivar' : 'Reactivar'}
                    </button>
                    <button class="btn-delete-agency admin-action-btn btn-danger" data-id="${a.id}" data-name="${a.name}" data-code="${a.code}" data-users="${a.usersCount || 0}" data-cases="${a.casesCount || 0}" title="Eliminar agencia definitivamente">
                      🗑️ Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    const formBox = area.querySelector('#form-new-agency-box') as HTMLElement;
    area.querySelector('#btn-toggle-new-agency')?.addEventListener('click', () => {
      formBox.style.display = formBox.style.display === 'none' ? 'block' : 'none';
    });
    area.querySelector('#btn-cancel-agency')?.addEventListener('click', () => {
      formBox.style.display = 'none';
    });

    // Delegación de eventos para inactivar/activar agencias
    area.querySelectorAll('.btn-toggle-agency').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const id = target.getAttribute('data-id');
        const currentActive = target.getAttribute('data-active') === 'true';
        const agencyName = target.getAttribute('data-name');

        const advertencia = currentActive 
          ? `¿Estás seguro de que deseas INACTIVAR la agencia "${agencyName}"?\n\nAl inactivarla:\n- Los usuarios y operadores de esta agencia NO podrán iniciar sesión.\n- Sus datos e historial no se borran, pero quedan congelados y aislados.`
          : `¿Deseas REACTIVAR la agencia "${agencyName}"?`;

        if (!confirm(advertencia)) return;

        try {
          const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/agencies/${id}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ active: !currentActive })
          });
          const data = await res.json();
          if (data.success) {
            await this.loadInitialData();
          } else {
            alert(`Error: ${data.message}`);
          }
        } catch (err: any) {
          alert(`Error de red: ${err.message}`);
        }
      });
    });

    // Eventos para eliminar definitivamente una agencia
    area.querySelectorAll('.btn-delete-agency').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const target = e.currentTarget as HTMLButtonElement;
        const id = target.getAttribute('data-id');
        const name = target.getAttribute('data-name');
        const code = target.getAttribute('data-code');
        const usersCount = parseInt(target.getAttribute('data-users') || '0', 10);
        const casesCount = parseInt(target.getAttribute('data-cases') || '0', 10);

        let warning = `¿Confirmas que deseas ELIMINAR permanentemente la agencia "${name}" (${code})?\n\n`;
        if (usersCount > 0 || casesCount > 0) {
          warning += `⚠️ Advertencia: Esta agencia tiene ${usersCount} usuario(s) y ${casesCount} caso(s) asociados.\nAl eliminarla, los usuarios y casos quedarán desvinculados para proteger la auditoría forense.\n\n`;
        }
        warning += `Esta acción borrará la agencia de la base de datos y NO se puede revertir.`;

        if (!confirm(warning)) return;

        try {
          const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/agencies/${id}`, {
            method: 'DELETE'
          });
          const data = await res.json();
          if (data.success) {
            alert(`✅ ${data.message || 'Agencia eliminada con éxito.'}`);
            await this.loadInitialData();
          } else {
            alert(`❌ Error al eliminar agencia: ${data.message}`);
          }
        } catch (err: any) {
          alert(`Error de red al eliminar: ${err.message}`);
        }
      });
    });

    area.querySelector('#btn-save-agency')?.addEventListener('click', async () => {
      const name = (area.querySelector('#inp-agency-name') as HTMLInputElement).value.trim();
      const code = (area.querySelector('#inp-agency-code') as HTMLInputElement).value.trim();
      const description = (area.querySelector('#inp-agency-desc') as HTMLInputElement).value.trim();

      if (!name || !code) {
        alert('Ingrese el nombre y código de la agencia.');
        return;
      }

      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/agencies`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, code, description })
        });
        const data = await res.json();
        if (data.success) {
          alert('Agencia dada de alta con éxito.');
          (area.querySelector('#inp-agency-name') as HTMLInputElement).value = '';
          (area.querySelector('#inp-agency-code') as HTMLInputElement).value = '';
          (area.querySelector('#inp-agency-desc') as HTMLInputElement).value = '';
          formBox.style.display = 'none';
          await this.loadInitialData();
        } else {
          alert(`Error: ${data.message}`);
        }
      } catch (e: any) {
        alert(`Error al guardar: ${e.message}`);
      }
    });
  }

  // --- 3. ROLES Y PERMISOS GRANULARES ---
  private renderRolesTab(area: Element): void {
    area.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:18px;">
        <div>
          <h2 style="margin:0; font-size:1.2rem;">Perfiles de Acceso y Permisos Granulares</h2>
          <p style="margin:4px 0 0; font-size:0.82rem; color:#94a3b8;">
            Define las capacidades específicas de cada perfil dentro del sistema para asignar a las agencias.
          </p>
        </div>
        <button id="btn-toggle-new-role" class="admin-btn-primary">
          + CREAR NUEVO PERFIL
        </button>
      </div>

      <!-- Formulario para crear rol -->
      <div id="form-new-role-box" style="display:none; background:#1e293b; border:1px solid #38bdf8; border-radius:12px; padding:20px; margin-bottom:20px;">
        <h3 style="margin:0 0 14px; font-size:1rem; color:#38bdf8;">Registrar Nuevo Perfil Personalizado</h3>
        <div style="display:grid; grid-template-columns: 2fr 1fr; gap:14px; margin-bottom:14px;">
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Nombre del Perfil</label>
            <input type="text" id="inp-role-name" class="form-input" placeholder="ej. Auditor Clínico" style="width:100%; box-sizing:border-box;" />
          </div>
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Código de Rol</label>
            <input type="text" id="inp-role-code" class="form-input" placeholder="ej. AUDITOR" style="width:100%; box-sizing:border-box;" />
          </div>
        </div>
        <div style="margin-bottom:16px;">
          <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Descripción de Responsabilidades</label>
          <input type="text" id="inp-role-desc" class="form-input" placeholder="ej. Revisa únicamente métricas y expedientes pasados sin despachar llamadas" style="width:100%; box-sizing:border-box;" />
        </div>

        <div style="background:#0f172a; border:1px solid #334155; border-radius:8px; padding:14px; margin-bottom:16px;">
          <div style="font-size:0.78rem; font-weight:700; color:#38bdf8; text-transform:uppercase; margin-bottom:10px;">Permisos Otorgados a este Perfil:</div>
          <div style="display:grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap:10px; font-size:0.82rem; color:#cbd5e1;">
            <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
              <input type="checkbox" id="chk-perm-dispatch" /> Iniciar videollamadas / Despacho 911
            </label>
            <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
              <input type="checkbox" id="chk-perm-cases" /> Consultar expedientes y forensia operativa
            </label>
            <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
              <input type="checkbox" id="chk-perm-videos" /> Reproducir videos grabados
            </label>
            <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
              <input type="checkbox" id="chk-perm-retention" /> Configurar retención y purga
            </label>
            <label style="display:flex; align-items:center; gap:6px; cursor:pointer;">
              <input type="checkbox" id="chk-perm-users" /> Administrar usuarios y roles
            </label>
          </div>
        </div>

        <div style="display:flex; justify-content:flex-end; gap:10px;">
          <button id="btn-cancel-role" class="admin-action-btn" style="background:transparent; border:1px solid #475569; color:#cbd5e1;">Cancelar</button>
          <button id="btn-save-role" class="admin-btn-success">Guardar Perfil</button>
        </div>
      </div>

      <!-- Tabla de Roles -->
      <div style="background:#1e293b; border:1px solid #334155; border-radius:10px; overflow:hidden;">
        <table style="width:100%; border-collapse:collapse; text-align:left; font-size:0.85rem;">
          <thead>
            <tr style="background:#0f172a; color:#94a3b8; font-size:0.75rem; text-transform:uppercase;">
              <th style="padding:12px 16px;">Código</th>
              <th style="padding:12px 16px;">Nombre del Perfil</th>
              <th style="padding:12px 16px;">Descripción</th>
              <th style="padding:12px 16px;">Permisos Clave</th>
            </tr>
          </thead>
          <tbody>
            ${this.roles.map(r => `
              <tr style="border-bottom:1px solid #334155;">
                <td style="padding:12px 16px; font-family:monospace; font-weight:700; color:#fbbf24;">${r.code}</td>
                <td style="padding:12px 16px; font-weight:700; color:#fff;">${r.name}</td>
                <td style="padding:12px 16px; color:#cbd5e1;">${r.description || '---'}</td>
                <td style="padding:12px 16px;">
                  <div style="display:flex; gap:6px; flex-wrap:wrap;">
                    ${r.can_dispatch ? '<span style="background:rgba(16,185,129,0.15); color:#34d399; font-size:0.7rem; padding:2px 6px; border-radius:4px;">Despacho</span>' : ''}
                    ${r.can_view_cases ? '<span style="background:rgba(56,189,248,0.15); color:#38bdf8; font-size:0.7rem; padding:2px 6px; border-radius:4px;">Expedientes</span>' : ''}
                    ${r.can_view_videos ? '<span style="background:rgba(147,51,234,0.15); color:#c084fc; font-size:0.7rem; padding:2px 6px; border-radius:4px;">Video</span>' : ''}
                    ${r.can_manage_retention ? '<span style="background:rgba(239,68,68,0.15); color:#fca5a5; font-size:0.7rem; padding:2px 6px; border-radius:4px;">Retención</span>' : ''}
                    ${r.can_manage_users ? '<span style="background:rgba(245,158,11,0.15); color:#fbbf24; font-size:0.7rem; padding:2px 6px; border-radius:4px;">Usuarios</span>' : ''}
                  </div>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    const formBox = area.querySelector('#form-new-role-box') as HTMLElement;
    area.querySelector('#btn-toggle-new-role')?.addEventListener('click', () => {
      formBox.style.display = formBox.style.display === 'none' ? 'block' : 'none';
    });
    area.querySelector('#btn-cancel-role')?.addEventListener('click', () => {
      formBox.style.display = 'none';
    });

    area.querySelector('#btn-save-role')?.addEventListener('click', async () => {
      const name = (area.querySelector('#inp-role-name') as HTMLInputElement).value.trim();
      const code = (area.querySelector('#inp-role-code') as HTMLInputElement).value.trim();
      const description = (area.querySelector('#inp-role-desc') as HTMLInputElement).value.trim();
      const canDispatch = (area.querySelector('#chk-perm-dispatch') as HTMLInputElement).checked;
      const canViewCases = (area.querySelector('#chk-perm-cases') as HTMLInputElement).checked;
      const canViewVideos = (area.querySelector('#chk-perm-videos') as HTMLInputElement).checked;
      const canManageRetention = (area.querySelector('#chk-perm-retention') as HTMLInputElement).checked;
      const canManageUsers = (area.querySelector('#chk-perm-users') as HTMLInputElement).checked;

      if (!name || !code) {
        alert('Complete nombre y código del perfil.');
        return;
      }

      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/roles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name, code, description, canDispatch, canViewCases, canViewVideos, canManageRetention, canManageUsers })
        });
        const data = await res.json();
        if (data.success) {
          alert('Perfil guardado exitosamente.');
          await this.loadInitialData();
        } else {
          alert(`Error: ${data.message}`);
        }
      } catch (e: any) {
        alert(`Error al guardar: ${e.message}`);
      }
    });
  }

  // --- 4. GESTIÓN DE USUARIOS Y ASIGNACIÓN A AGENCIA ---
  private renderUsersTab(area: Element): void {
    area.innerHTML = `
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:18px;">
        <div>
          <h2 style="margin:0; font-size:1.2rem;">Directorio de Usuarios y Operadores</h2>
          <p style="margin:4px 0 0; font-size:0.82rem; color:#94a3b8;">
            Asigna a cada usuario su agencia correspondiente. Un usuario asignado a Cruz Roja solo verá eventos de Cruz Roja.
          </p>
        </div>
        <button id="btn-toggle-new-user" class="admin-btn-primary">
          + REGISTRAR USUARIO
        </button>
      </div>

      <!-- Formulario para crear usuario -->
      <div id="form-new-user-box" style="display:none; background:#1e293b; border:1px solid #38bdf8; border-radius:12px; padding:20px; margin-bottom:20px;">
        <h3 style="margin:0 0 14px; font-size:1rem; color:#38bdf8;">Registrar Nueva Cuenta con Asignación de Agencia</h3>
        <div style="display:grid; grid-template-columns: 1fr 1fr 1fr; gap:14px; margin-bottom:14px;">
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Usuario (Login)</label>
            <input type="text" id="inp-user-username" class="form-input" placeholder="ej. paramedico_cr" style="width:100%; box-sizing:border-box;" />
          </div>
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Nombre Completo</label>
            <input type="text" id="inp-user-fullname" class="form-input" placeholder="ej. María Morales G." style="width:100%; box-sizing:border-box;" />
          </div>
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">No. de Placa / Credencial</label>
            <input type="text" id="inp-user-badge" class="form-input" placeholder="ej. CR-304" style="width:100%; box-sizing:border-box;" />
          </div>
        </div>

        <div style="display:grid; grid-template-columns: 1fr 1fr 1fr; gap:14px; margin-bottom:16px;">
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Agencia / Institución Asignada</label>
            <select id="sel-user-agency" class="form-input" style="width:100%; box-sizing:border-box; background:#0f172a; color:#fff;">
              ${this.agencies.map(a => `<option value="${a.id}">${a.name} (${a.code})</option>`).join('')}
            </select>
          </div>
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Perfil / Rol</label>
            <select id="sel-user-role" class="form-input" style="width:100%; box-sizing:border-box; background:#0f172a; color:#fff;">
              ${this.roles.map(r => `<option value="${r.code}">${r.name} (${r.code})</option>`).join('')}
            </select>
          </div>
          <div>
            <label style="font-size:0.78rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:4px;">Contraseña</label>
            <input type="password" id="inp-user-pass" class="form-input" placeholder="••••••••" style="width:100%; box-sizing:border-box;" />
          </div>
        </div>

        <div style="display:flex; justify-content:flex-end; gap:10px;">
          <button id="btn-cancel-user" class="admin-action-btn" style="background:transparent; border:1px solid #475569; color:#cbd5e1;">Cancelar</button>
          <button id="btn-save-user" class="admin-btn-success">Crear Usuario</button>
        </div>
      </div>

      <!-- Tabla de Usuarios -->
      <div style="background:#1e293b; border:1px solid #334155; border-radius:10px; overflow:hidden;">
        <table style="width:100%; border-collapse:collapse; text-align:left; font-size:0.85rem;">
          <thead>
            <tr style="background:#0f172a; color:#94a3b8; font-size:0.75rem; text-transform:uppercase;">
              <th style="padding:12px 16px;">Usuario</th>
              <th style="padding:12px 16px;">Nombre</th>
              <th style="padding:12px 16px;">Placa</th>
              <th style="padding:12px 16px;">Agencia Asignada</th>
              <th style="padding:12px 16px;">Rol</th>
              <th style="padding:12px 16px;">Estado</th>
              <th style="padding:12px 16px; text-align:right;">Acción</th>
            </tr>
          </thead>
          <tbody>
            ${this.users.map(u => `
              <tr style="border-bottom:1px solid #334155;">
                <td style="padding:12px 16px; font-weight:700; color:#fff;">@${u.username}</td>
                <td style="padding:12px 16px; color:#cbd5e1;">${u.fullName}</td>
                <td style="padding:12px 16px; color:#94a3b8; font-family:monospace;">${u.badgeNumber || '---'}</td>
                <td style="padding:12px 16px;">
                  <span style="background:rgba(56,189,248,0.12); color:#38bdf8; font-weight:700; font-size:0.75rem; padding:3px 8px; border-radius:4px;">
                    ${u.agencyName || 'Agencia Principal'}
                  </span>
                </td>
                <td style="padding:12px 16px;">
                  <span style="background:rgba(255,255,255,0.08); font-size:0.75rem; font-weight:700; padding:3px 8px; border-radius:4px; color:${u.role === 'ADMIN' ? '#fbbf24' : (u.role === 'SUPERVISOR' ? '#38bdf8' : '#34d399')};">
                    ${u.role}
                  </span>
                </td>
                <td style="padding:12px 16px;">
                  <span style="color:${u.active ? '#10b981' : '#ef4444'}; font-weight:700; font-size:0.8rem;">
                    ● ${u.active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td style="padding:12px 16px; text-align:right;">
                  <button class="btn-toggle-user-status admin-action-btn ${u.active ? 'btn-warn' : 'btn-success'}" data-id="${u.id}" data-active="${u.active}">
                    ${u.active ? 'Desactivar' : 'Activar'}
                  </button>
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    const formBox = area.querySelector('#form-new-user-box') as HTMLElement;
    area.querySelector('#btn-toggle-new-user')?.addEventListener('click', () => {
      formBox.style.display = formBox.style.display === 'none' ? 'block' : 'none';
    });
    area.querySelector('#btn-cancel-user')?.addEventListener('click', () => {
      formBox.style.display = 'none';
    });

    area.querySelector('#btn-save-user')?.addEventListener('click', async () => {
      const username = (area.querySelector('#inp-user-username') as HTMLInputElement).value.trim();
      const fullName = (area.querySelector('#inp-user-fullname') as HTMLInputElement).value.trim();
      const badgeNumber = (area.querySelector('#inp-user-badge') as HTMLInputElement).value.trim();
      const agencyId = (area.querySelector('#sel-user-agency') as HTMLSelectElement).value;
      const role = (area.querySelector('#sel-user-role') as HTMLSelectElement).value;
      const password = (area.querySelector('#inp-user-pass') as HTMLInputElement).value;

      if (!username || !fullName || !password) {
        alert('Complete los campos requeridos.');
        return;
      }

      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, fullName, badgeNumber, agencyId, role, password })
        });
        const data = await res.json();
        if (data.success) {
          alert('Usuario registrado.');
          await this.loadInitialData();
        } else {
          alert(`Error: ${data.message}`);
        }
      } catch (e: any) {
        alert(`Error al guardar: ${e.message}`);
      }
    });

    area.querySelectorAll('.btn-toggle-user-status').forEach(btn => {
      btn.addEventListener('click', async () => {
        const id = btn.getAttribute('data-id');
        const currentActive = btn.getAttribute('data-active') === 'true';
        try {
          const res = await fetch(`${this.apiBaseUrl}/api/v1/auth/users/${id}/status`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ active: !currentActive })
          });
          if (res.ok) await this.loadInitialData();
        } catch (_) {}
      });
    });
  }

  // --- 5. POLÍTICAS DE ALMACENAMIENTO Y RETENCIÓN ---
  private renderStorageTab(area: Element): void {
    area.innerHTML = `
      <div style="max-width:700px; background:#1e293b; border:1px solid #334155; border-radius:12px; padding:24px;">
        <h2 style="margin:0 0 8px; font-size:1.2rem;">Políticas de Almacenamiento & Depuración Física</h2>
        <p style="margin:0 0 20px; font-size:0.82rem; color:#94a3b8;">
          Reglas de custodia forense bajo estándares de salud y seguridad. Los archivos vencidos se eliminan físicamente para proteger el espacio en disco.
        </p>

        <div style="display:flex; flex-direction:column; gap:18px;">
          <div>
            <label style="font-size:0.82rem; font-weight:700; color:#cbd5e1; display:block; margin-bottom:6px;">
              Período de Conservación Obligatoria (Días)
            </label>
            <div style="display:flex; align-items:center; gap:10px;">
              <input type="number" id="inp-retention-days" value="${this.retentionPolicy.retentionDays}" min="1" max="1825" class="form-input" style="width:140px;" />
              <span style="font-size:0.85rem; color:#94a3b8;">días calendario</span>
            </div>
          </div>

          <div style="display:flex; align-items:center; gap:10px; background:#0f172a; padding:12px 14px; border-radius:8px; border:1px solid #334155;">
            <input type="checkbox" id="chk-auto-purge" ${this.retentionPolicy.autoPurgeEnabled ? 'checked' : ''} style="width:20px; height:20px; cursor:pointer;" />
            <label for="chk-auto-purge" style="font-size:0.85rem; color:#cbd5e1; cursor:pointer;">
              Activar tarea automatizada de purga (Cron diario a las 03:00 hrs)
            </label>
          </div>

          <div style="display:flex; gap:12px; margin-top:10px;">
            <button id="btn-save-retention-policy" class="admin-btn-primary">
              Guardar Política de Retención
            </button>
            <button id="btn-force-purge-now" class="admin-action-btn btn-danger" style="padding:10px 18px; font-size:0.84rem;">
              🗑️ Ejecutar Purga Inmediata en Disco
            </button>
          </div>
        </div>
      </div>
    `;

    area.querySelector('#btn-save-retention-policy')?.addEventListener('click', async () => {
      const days = parseInt((area.querySelector('#inp-retention-days') as HTMLInputElement).value, 10);
      const autoPurge = (area.querySelector('#chk-auto-purge') as HTMLInputElement).checked;

      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/media/retention-policy`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ retentionDays: days, autoPurgeEnabled: autoPurge })
        });
        if (res.ok) alert('Política de retención guardada.');
      } catch (e: any) {
        alert('Error guardando política.');
      }
    });

    area.querySelector('#btn-force-purge-now')?.addEventListener('click', async () => {
      if (!confirm('¿Confirma la depuración inmediata de todos los videos vencidos en disco?')) return;
      try {
        const res = await fetch(`${this.apiBaseUrl}/api/v1/media/retention-policy/purge-now`, { method: 'POST' });
        const data = await res.json();
        alert(data.message || 'Purga ejecutada.');
        await this.loadInitialData();
      } catch (e: any) {
        alert('Error ejecutando purga.');
      }
    });
  }
}
